import { useMemo } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';

import { PageBoundingBox } from '../../../../../../../../backend/src/matter/fact/fact-bounding-box';
import { useGetProceduralFile } from '@/api/queries/useGetProceduralFile';
import { Icon } from '@/new/components/icon';
import { PDFViewer } from '@/new/components/pdf-viewer';
import { ResizableSidePanel } from '@/new/components/resizable-side-panel';
import { Button } from '@/new/components/shadcn/button';
import { BoundingBoxWithId } from '@/types';

export interface ProceduralSourceRef {
  fileId: string;
  fileName: string;
  pageNumber: number;
  chunkId: string;
  content: string;
  boundingBox: PageBoundingBox;
}

interface ProceduralSourcePanelProps {
  source: ProceduralSourceRef | null;
  onClose: () => void;
}

/**
 * Inline source panel rendered as a flex sibling to the timeline. Uses the
 * shared ResizableSidePanel so viewers on large screens can drag it wider, the
 * timeline column shrinks when it opens, and the PDF viewer fits the available
 * height instead of stacking under a page-level scroll container.
 */
export function ProceduralSourcePanel({ source, onClose }: ProceduralSourcePanelProps) {
  return (
    <AnimatePresence initial={false}>
      {source && (
        <ResizableSidePanel
          storageKey="procedural-source"
          onClose={onClose}
          className="rounded-lg border"
          disableDrawerMode
        >
          <ProceduralSourcePanelBody source={source} onClose={onClose} />
        </ResizableSidePanel>
      )}
    </AnimatePresence>
  );
}

function ProceduralSourcePanelBody({ source, onClose }: { source: ProceduralSourceRef; onClose: () => void }) {
  const { matterId } = useParams();
  const { data: file, isLoading, error } = useGetProceduralFile(matterId, source.fileId);

  const highlight: BoundingBoxWithId = useMemo(
    () => ({ ...source.boundingBox, id: source.chunkId }),
    [source.boundingBox, source.chunkId],
  );

  const fileMetadata = useMemo(
    () => (file ? { name: file.name, extension: file.extension, pageDimensions: file.pageDimensions } : undefined),
    [file],
  );

  return (
    <>
      <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-3">
        <Icon className="lucide--file-text mt-0.5 shrink-0 text-gray-500" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">{source.fileName}</p>
          <p className="text-xs text-gray-500">Page {source.pageNumber}</p>
        </div>
        <Button onClick={onClose} variant="ghost" size="iconSm" aria-label="Close source panel">
          <Icon className="lucide--x" />
        </Button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {error && (
          <div className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-500">
            Failed to load the source document.
          </div>
        )}
        {!error && (isLoading || !file) && (
          <div className="flex h-full items-center justify-center">
            <Icon className="lucide--loader-2 animate-spin text-gray-400" />
          </div>
        )}
        {file && (
          <PDFViewer
            docId={file.id}
            fileUrl={file.url}
            fileMetadata={fileMetadata}
            highlights={[highlight]}
            highlightedFactId={source.chunkId}
            hideFeatureButtons={true}
            hideSnippingTool={true}
            hideDownload={true}
            hideFactInfo={true}
            fileName={file.name}
          />
        )}
      </div>
    </>
  );
}
